import { AdVertical } from "@/sell/models/AdVerticals";
import { AdCheckout } from "@/sell/models/checkout/schema/AdCheckout";
import {
  AdProductConfig,
  GetAllProductConfigs,
} from "@/sell/models/checkout/config/AdProductConfig";
import { CheckoutPackageService } from "./CheckoutPackageService";
import {
  AD_PACKAGE_PRICING,
  AdPackageConfig,
  AdPackagePricing,
} from "@/sell/models/checkout/config/AdPackageConfig";
import { AdPackageState } from "@/sell/models/checkout/state/AdPackageState";
import { AdProductState } from "@/sell/models/checkout/state/AdProductState";
import {
  AD_PACKAGE_KEYS,
  AdPackageKey,
} from "@/sell/models/checkout/schema/AdPackageKey";
import { AdUpgradeState } from "@/sell/models/checkout/state/AdUpgradeState";
import { CheckoutUpgradeService } from "./CheckoutUpgradeService";
import { AdUpgradeChoiceKey } from "@/sell/models/checkout/schema/AdProductChoiceKey";
import {
  AdCheckoutState,
  AdDiscountOrderLine,
  AdOrderLine,
  AdProductOrderLine,
} from "@/sell/models/checkout/state/AdCheckoutState";
import _ from "lodash";
import { CurrencyService } from "./CurrencyService";
import { AdConfigState } from "@/sell/models/AdConfigState";
import { AdCheckoutModel } from "@/sell/models/AdCheckoutModel";
import { AdDetails, AdOfferDetails } from "@/sell/models/AdDetails";
import { UserWallet } from "@/sell/models/UserWalletViewModel";

function EncodeProduct({
  checkout,
  config,
  product,
  pastPackage,
  pendingPackage,
}: {
  checkout: AdCheckout;
  config: AdConfigState;
  product: AdProductConfig;
  pastPackage?: AdPackageConfig;
  pendingPackage?: AdPackageConfig;
}): AdProductState | undefined {
  if (product.type === "PACKAGE") {
    const state: AdPackageState = {
      ...product,
      status: CheckoutPackageService.GetStatusForPackage({
        product,
        pastPackage: pastPackage,
        pendingPackage: pendingPackage,
      }),
      isSelected: false,
      action: "NONE",
      price: CurrencyService.EncodeCents(0),
    };
    return state;
  } else if (product.type === "UPGRADE") {
    const state: AdUpgradeState = {
      ...product,
      ...CheckoutUpgradeService.GetState({
        checkout,
        config,
        upgrade: product,
        pendingPackage,
        purchasedPackage: pastPackage,
      }),
    };
    return state;
  }
}

function SortDescendingBy<T>(array: T[], selector: (item: T) => number) {
  array.sort((a, b) => selector(b) - selector(a));
}

function GetPackageStates({
  productConfigs,
  checkout,
  config,
}: {
  productConfigs: AdProductConfig[];
  checkout: AdCheckout;
  config: AdConfigState;
}): {
  pendingPackage?: AdPackageConfig;
  pastPackage?: AdPackageConfig;
  minimumPackage?: AdPackageConfig;
} {
  const packages = productConfigs.filter((p) => p.type === "PACKAGE");
  const pendingPackage = packages.find((p) =>
    checkout.pending.map((pending) => pending.key.product).includes(p.key),
  );
  const pastPurchaseKeys = config.purchases.map((past) => past.key.product);
  const pastPackages = packages.filter((p) => pastPurchaseKeys.includes(p.key));
  const availablePackages = packages.filter(
    (p) => !pastPurchaseKeys.includes(p.key),
  );
  SortDescendingBy(pastPackages, (t) => t.value);
  const pastPackage = pastPackages.length > 0 ? pastPackages[0] : undefined;
  SortDescendingBy(availablePackages, (t) => t.value);
  const minimumAvailablePackage =
    availablePackages.length > 0
      ? availablePackages[availablePackages.length - 1]
      : undefined;
  return {
    pendingPackage,
    pastPackage,
    minimumPackage: minimumAvailablePackage || pastPackage,
  };
}

function HydrateSelectedPackageAndActions({
  vertical,
  pastPackage,
  productStates,
  details,
  config,
}: {
  vertical: AdVertical;
  pastPackage?: AdPackageConfig;
  productStates: AdProductState[];
  details: AdDetails;
  config: AdConfigState;
}) {
  const packages = productStates.filter((p) => p.type === "PACKAGE");
  SortDescendingBy(packages, (t) => t.value);
  const addedPackage = packages.find((t) => t.status === "ADDED");
  const lastPurchasedPackage = packages.find(
    (t) => t.status === "UNAVAILABLE_PURCHASED",
  );
  const selectedPackage = addedPackage ? addedPackage : lastPurchasedPackage;
  if (selectedPackage) {
    selectedPackage.isSelected = true;
  }

  packages.forEach((product: AdPackageState) => {
    if (!selectedPackage) {
      product.action = "ADD";
    } else if (
      product.value < selectedPackage.value &&
      (product.status === "INCLUDED" ||
        product.status === "AVAILABLE" ||
        product.key === pastPackage?.key)
    ) {
      product.action = "DOWNGRADE";
    } else if (product.value > selectedPackage.value) {
      product.action = "UPGRADE";
    } else if (
      product.value === selectedPackage.value &&
      selectedPackage.status === "ADDED" &&
      pastPackage
    ) {
      product.action = "REMOVE";
    } else {
      product.action = "NONE";
    }

    if (CheckoutService.IsConditionallyFree({ product, vertical, details })) {
      product.price = CurrencyService.EncodeDollars(0);
      product.isConditionallyFree = true;
      return;
    }
    const packagePrice = CheckoutService.GetAdPackagePrice({
      product,
      config,
      vertical,
      details,
    });
    const pastPackagePrice = pastPackage
      ? CheckoutService.GetAdPackagePrice({
          product: pastPackage,
          config,
          vertical,
          details,
        })
      : 0;
    product.price = CurrencyService.EncodeDollars(
      packagePrice - pastPackagePrice,
    );
    if (product.price.amountAUCents <= 0) {
      product.price = CurrencyService.EncodeDollars(0);
    }
  });
}

function EncodeProductOrderLines({
  productStates,
}: {
  productStates: AdProductState[];
}): AdProductOrderLine[] {
  const purchaseProducts = productStates.filter((x) => x.status === "ADDED");

  const purchases = purchaseProducts.map((p): AdProductOrderLine => {
    if (p.type === "PACKAGE") {
      return {
        id: p.key,
        type: "product",
        key: {
          type: p.type,
          product: p.key,
          variant: "NONE",
        },
        label: p.title + " Ad",
        amount: CurrencyService.EncodeCents(p.price.amountAUCents),
        isConditionallyFree: p.isConditionallyFree,
      };
    } else if (p.type === "UPGRADE") {
      const variant = p.variants.find(
        (x) => x.status === "ADDED" || x.status === "INCLUDED",
      );
      if (!variant) {
        throw new Error(`No variant selected for upgrade ${p.key}`);
      }
      return {
        type: "product",
        id: `${p.key}-${variant.key}`,
        key: {
          type: p.type,
          product: p.key,
          variant: variant.key,
        },
        label: `${p.title} ${variant.label ? ` (${variant.label})` : ""}`,
        amount: CurrencyService.EncodeCents(p.price.amountAUCents),
      };
    } else {
      throw new Error(`Unsupported product type`);
    }
  });

  return purchases;
}

export const CheckoutService = {
  GetPendingAfterAddingPackage: ({
    checkout,
    config,
    key,
  }: {
    checkout: AdCheckout;
    config: AdConfigState;
    key: AdPackageKey;
  }) => {
    const allProducts = GetAllProductConfigs({
      pricing: config.pricing,
    });
    let newPending = [...checkout.pending];

    const productToAdd = allProducts.find((p) => p.key === key);

    if (!productToAdd || productToAdd.type !== "PACKAGE") {
      return newPending;
    }

    // Make sure there's only ever one package selected
    const packageKeys = AD_PACKAGE_KEYS.map((x) => x.toString());
    newPending = newPending.filter(
      (pending) => !packageKeys.includes(pending.key.product),
    );

    // Don't allow a package to be readded if it's already been purchased
    const pastPurchase = config.purchases.find((p) => p.key.product === key);
    if (pastPurchase) {
      return newPending;
    }

    if (productToAdd) {
      newPending.push({
        key: {
          type: "PACKAGE",
          product: productToAdd.key,
          variant: "NONE",
        },
        timestamp: new Date(),
      });
    }

    return newPending;
  },
  GetPendingAfterTogglingUpgrade: ({
    checkout,
    config,
    key,
  }: {
    checkout: AdCheckout;
    config: AdConfigState;
    key: AdUpgradeChoiceKey;
  }) => {
    const allProducts = GetAllProductConfigs({
      pricing: config.pricing,
    });
    let newPending = [...checkout.pending];

    const productToAdd = allProducts.find((p) => p.key === key.product);

    if (!productToAdd || productToAdd.type !== "UPGRADE") {
      return newPending;
    }

    const existingPending = newPending.find(
      (pending) => pending.key.product === productToAdd.key,
    );

    newPending = newPending.filter(
      (pending) => pending.key.product !== productToAdd.key,
    );
    if (!existingPending) {
      newPending.push({
        key: {
          type: "UPGRADE",
          product: productToAdd.key,
          variant: key.variant,
        },
        timestamp: new Date(),
      });
    }

    return newPending;
  },
  GetCheckoutState: (
    { checkout, config, vertical, details }: AdCheckoutModel,
    wallet?: UserWallet,
  ): AdCheckoutState => {
    const productConfigs = GetAllProductConfigs({
      pricing: config.pricing,
    });

    const { pendingPackage, pastPackage } = GetPackageStates({
      productConfigs,
      checkout,
      config,
    });

    const productStates = productConfigs
      .map((product) =>
        EncodeProduct({
          checkout,
          config,
          product,
          pastPackage,
          pendingPackage,
        }),
      )
      .filter((x) => x !== undefined);

    HydrateSelectedPackageAndActions({
      vertical,
      pastPackage,
      productStates,
      details,
      config,
    });

    const productOrderLines = EncodeProductOrderLines({
      productStates,
    });

    const totalBeforeDiscountsAUCents = _.sumBy(
      productOrderLines,
      (p) => p.amount.amountAUCents,
    );

    const discountOrderLines = checkout.discounts.map(
      (discount): AdDiscountOrderLine => {
        return {
          id: discount.id,
          type: "discount",
          label: discount.label,
          amount: CurrencyService.EncodeCents(
            (totalBeforeDiscountsAUCents * -discount.discountPercentage) / 100,
          ),
        };
      },
    );

    const orderLines: AdOrderLine[] = [
      ...productOrderLines,
      ...discountOrderLines,
    ];

    const originalTotalAUCents = _.sumBy(
      orderLines,
      (p) => p.amount.amountAUCents,
    );
    if (originalTotalAUCents > 0 && wallet?.balanceCentsAUD) {
      const accountPaymentAmount = Math.min(
        wallet.balanceCentsAUD,
        originalTotalAUCents,
      );
      orderLines.push({
        id: "wallet_payment",
        type: "wallet_payment",
        label: `Your Wallet (Current Balance ${CurrencyService.EncodeCents(wallet.balanceCentsAUD).display})`,
        amount: CurrencyService.EncodeCents(-accountPaymentAmount),
      });
    }

    const totalAUCents = _.sumBy(orderLines, (p) => p.amount.amountAUCents);

    const approvalRequired =
      orderLines.some((line) => line.isConditionallyFree === true) &&
      totalAUCents === 0;

    return {
      products: productStates,
      orderLines: orderLines,
      status:
        totalAUCents > 0
          ? "PAYMENT_REQUIRED"
          : approvalRequired
            ? "APPROVAL_REQUIRED"
            : "NONE",
      total: CurrencyService.EncodeCents(totalAUCents),
    };
  },
  GetAdPackagePrice: ({
    product,
    config,
    vertical,
    details,
  }: {
    product: AdPackageConfig;
    config: AdConfigState;
    vertical: AdVertical;
    details: AdOfferDetails;
  }): number => {
    if (CheckoutService.IsConditionallyFree({ product, vertical, details })) {
      return 0;
    }
    const packagePricing = CheckoutService.GetAdDynamicPrices({
      config,
      vertical,
      details,
    })?.adPriceRanges.find(
      (range) =>
        Number(details.price) >= range.min &&
        Number(details.price) <= range.max,
    );
    const dynamicPricing = packagePricing?.prices[product.key];
    const staticPricing = product.prices[vertical];
    return dynamicPricing !== undefined ? dynamicPricing : staticPricing;
  },
  GetAdDynamicPrices: ({
    config,
    vertical,
    details,
  }: {
    config: AdConfigState;
    vertical: AdVertical;
    details: AdOfferDetails;
  }): AdPackagePricing | undefined => {
    return AD_PACKAGE_PRICING.find(
      (p) =>
        p.adPricingModels.includes(config.pricing) &&
        p.adVerticals.includes(vertical) &&
        p.adTypes.includes(details.type) &&
        p.adPriceTypes.includes(details.priceType),
    );
  },
  IsConditionallyFree: ({
    product,
    vertical,
    details,
  }: {
    product: AdPackageConfig;
    vertical: AdVertical;
    details: AdOfferDetails;
  }): boolean => {
    const freeCondition = product.pricesFreeConditions?.find(
      (x) =>
        x.vertical === vertical &&
        details.priceType !== "poa" &&
        Number(details.price) < x.maxPrice,
    );
    return !!freeCondition;
  },
};
